import { queryOptions, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { SECONDS_IN_DAY } from '@cast/constants';
import type { DateRange } from '@cast/shared-types';
import { NoDataReason } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';
import { WorkloadCostReport } from 'features/cost-report/types/workloadsCostReport';

const WORKLOAD_COST_REPORT_QUERY_KEY = 'workload-cost-report';

const enhanceWorkloadCost = (item: WorkloadCostReport) => {
  return {
    ...item,
    costMetrics: item.costMetrics.map((metric) => {
      return {
        ...metric,
        // need to subtract 1 day for each record, because BE aggregates data in such a way that the end of the day is returned when we request for daily records
        timestamp: dayjs.utc(metric.timestamp).subtract(1, 'd').toISOString(),
      };
    }),
  };
};

type UseWorkloadCostReportQuery = {
  clusterId: string;
  namespace: string;
  workloadType: string;
  workloadName: string;
  range: DateRange;
  useListingPrices: boolean;
  stepSeconds?: number;
  enabled?: boolean;
  includeIdleCosts?: boolean;
};

type WorkloadCostReportQuery = {
  workload?: WorkloadCostReport;
  noDataReason?: NoDataReason;
  isLoading: boolean;
  isFetching: boolean;
  error?: unknown;
  refetch: () => void;
};

const getWorkloadCostReportQueryKey = ({
  clusterId,
  namespace,
  workloadType,
  workloadName,
  from,
  to,
  useListingPrices,
  stepSeconds,
  includeIdleCosts,
}: {
  clusterId: string;
  namespace: string;
  workloadType: string;
  workloadName: string;
  from: { toISOString(): string };
  to: { toISOString(): string };
  useListingPrices: boolean;
  stepSeconds: number;
  includeIdleCosts: boolean;
}) =>
  [
    WORKLOAD_COST_REPORT_QUERY_KEY,
    clusterId,
    namespace,
    workloadType,
    workloadName,
    from.toISOString(),
    to.toISOString(),
    useListingPrices,
    stepSeconds,
    includeIdleCosts,
  ] as const;

const getWorkloadCostReportQueryOptions = ({
  clusterId,
  namespace,
  workloadType,
  workloadName,
  range: [from, to],
  useListingPrices,
  stepSeconds = SECONDS_IN_DAY,
  enabled = true,
  includeIdleCosts = false,
}: UseWorkloadCostReportQuery) =>
  queryOptions({
    queryKey: getWorkloadCostReportQueryKey({
      clusterId,
      namespace,
      workloadType,
      workloadName,
      from,
      to,
      useListingPrices,
      stepSeconds,
      includeIdleCosts,
    }),
    queryFn: async () => {
      const { data } = await apiClient.costReport.getSingleWorkloadCostReport(
        {
          clusterId,
          namespace,
          workloadType,
          workloadName,
          startTime: from.toISOString(),
          endTime: to.toISOString(),
          stepSeconds,
          useListingPrices,
          includeIdleResourceCosts: includeIdleCosts,
        },
        { timeout: 120_000 }
      );

      return {
        item: data.item
          ? enhanceWorkloadCost(data.item as WorkloadCostReport)
          : undefined,
        noDataReason: data.noDataReason,
      };
    },
    enabled: !!clusterId && enabled,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

export const useWorkloadCostReportQuery = (
  props: UseWorkloadCostReportQuery
): WorkloadCostReportQuery => {
  const { isLoading, isFetching, data, error, refetch } = useQuery(
    getWorkloadCostReportQueryOptions(props)
  );

  return {
    workload: data?.item,
    noDataReason: data?.noDataReason,
    isLoading,
    isFetching,
    error,
    refetch,
  };
};
